import { memo, useState } from "react";
import BreadCrumbs from "@Components/BreadCrumbs";
import MenuDropdown from "@Components/MenuDropdown";
import styles from "./ContentGridTop.module.scss";
import wrapperHOC from "@Utils/wrapperHOC";
import Chevron from "@Icons/Chevron";
import useTranslationHook from "hooks/useTranslationHook";

interface ContentGridTopProps {
  itemName: string;
  itemSlug: string;
  itemType: "category" | "tag" | "search" | "latest";
  basePath: string;
  contentLabel: string;
  breadcrumbLabel: string;
  searchQuery?: string;
  totalItems: number;
  onSortChange: (sortType: string) => void;
}

const ContentGridTop = ({
  itemName,
  itemSlug,
  itemType,
  basePath,
  contentLabel,
  breadcrumbLabel,
  searchQuery = "",
  totalItems,
  onSortChange,
}: ContentGridTopProps) => {
  const [selectedSort, setSelectedSort] = useState("newest");
  const { t } = useTranslationHook();
  const handleSortSelect = (sortType: string) => {
    setSelectedSort(sortType);
    onSortChange(sortType);
  };

  const renderCountText = () => {
    if (itemType === "latest") {
      return (
        <>
          <span className={styles.countPrefix}>{t("blog.grid.showingthelatest")} </span>
          <span className={styles.countNumber}>{totalItems}</span>
          <span className={styles.countSuffix}> {contentLabel}</span>
        </>
      );
    }
    if (totalItems === 0) {
      return <span className={styles.countText}>{t("blog.grid.nofound", { contentLabel })}</span>;
    }
    if (totalItems === 1) {
      return (
        <>
          <span className={styles.countPrefix}>{t("blog.grid.showing")} </span>
          <span className={styles.countNumber}>1</span>
          <span className={styles.countSuffix}>
            {" "}
            {contentLabel.endsWith("s") ? contentLabel.slice(0, -1) : contentLabel}
          </span>
        </>
      );
    }
    return (
      <>
        <span className={styles.countPrefix}>{t("blog.grid.showing")} </span>
        <span className={styles.countNumber}>{totalItems}</span>
        <span className={styles.countSuffix}> {contentLabel}</span>
      </>
    );
  };

  const sortOptions = [
    { label: "blog.grid.newestfirst", value: "newest" },
    { label: "blog.grid.oldestfirst", value: "oldest" },
  ];

  let breadcrumbPath: string;
  if (itemType === "search") {
    breadcrumbPath = `${basePath}/search?query=${encodeURIComponent(searchQuery)}`;
  } else if (itemType === "tag") {
    breadcrumbPath = `${basePath}/tag/${itemSlug}`;
  } else if (itemType === "latest") {
    breadcrumbPath = `${basePath}/latest`;
  } else {
    breadcrumbPath = `${basePath}/category/${itemSlug}`;
  }

  let breadcrumbName: string;
  if (itemType === "search") {
    breadcrumbName = t("blog.grid.searchresults");
  } else if (itemType === "latest") {
    breadcrumbName = `${t("blog.grid.latest")} ${breadcrumbLabel}`;
  } else {
    breadcrumbName = itemName;
  }

  let titlePrefix: string;
  if (itemType === "search") {
    titlePrefix = t("blog.grid.searchresultsfor");
  } else if (itemType === "tag") {
    titlePrefix = `${breadcrumbLabel} ${t("blog.grid.taggedwith")}`;
  } else if (itemType === "latest") {
    titlePrefix = t("blog.grid.all");
  } else {
    titlePrefix = `${breadcrumbLabel} ${t("blog.grid.relatedto")}`;
  }

  let displayName: string;
  if (itemType === "search") {
    displayName = searchQuery;
  } else if (itemType === "latest") {
    displayName = `${t("blog.grid.latest")} ${breadcrumbLabel}`;
  } else {
    displayName = itemName;
  }

  return (
    <div className={styles.headerContainer}>
      <BreadCrumbs
        data={[
          { link: "/", name: t("blog.grid.breadcrumb.home") },
          { link: basePath, name: t(breadcrumbLabel) },
          { link: breadcrumbPath, name: breadcrumbName },
        ]}
      />
      <div className={styles.titleRow}>
        <div className={styles.titleSection}>
          {itemType !== "latest" && (
            <>
              <span className={styles.titlePrefix}>{titlePrefix}</span>
              <span className={styles.titleText}>&quot;{t(displayName)}&quot;</span>
              <span className={styles.separator}>|</span>
            </>
          )}
          <span className={styles.countText}>{renderCountText()}</span>
        </div>
        {totalItems > 0 ? (
          <MenuDropdown
            target={
              <button
                className={`${styles.sortButton} ${
                  selectedSort === "oldest" ? styles.sortButtonActive : ""
                }`}
                type="button"
              >
                <span className={styles.sortText}>{t("blog.grid.sortby")}</span>
                <div className={styles.chevron}>
                  <Chevron height="12" width="12" />
                </div>
              </button>
            }
            menu={
              <div className={styles.sortMenu}>
                {sortOptions.map((option) => (
                  <div
                    key={option.value}
                    className={`${styles.sortOption} ${
                      selectedSort === option.value ? styles.selected : ""
                    }`}
                    onClick={() => handleSortSelect(option.value)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleSortSelect(option.value);
                      }
                    }}
                  >
                    {t(option.label)}
                  </div>
                ))}
              </div>
            }
            direction="right"
            closeOnSelect
          />
        ) : (
          <button
            className={styles.sortButton}
            type="button"
            disabled
            style={{ visibility: "hidden" }}
          >
            <span className={styles.sortText}>{t("blog.grid.sortby")}</span>
            <div className={styles.chevron}>
              <Chevron height="12" width="12" />
            </div>
          </button>
        )}
      </div>
    </div>
  );
};

export default wrapperHOC(memo(ContentGridTop), {
  componentName: "ContentGridTop",
  showForChina: true,
});
